import ShareSearchInputSection from './ShareSearchInputSection';
import ShareSubmitButton from './ShareSubmitButton';
import CopyLinkButton from '@/components/Record/ShareButton/useShareModal/CopyLinkButton';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { InviteContainer } from './styles';
import useClickOutside from '@/components/Record/SubjectContent/@hooks/useClickOutside';
import { useFormContext } from 'react-hook-form';
import {
  InternalParticipant,
  RecordFilter,
  ShareMode,
  ShareModeParam,
  isRecentSharePassportTarget,
} from '@/types/api';
import {
  usePassportSearch,
  useSetTalkFlowShareMode,
  useTalkFlowShareList,
} from '@/hooks/apiHooks';
import {
  addRecentSharedPassports,
  useRecentShareTargets,
} from '@/hooks/apiHooks/talk_flow';
import { useDebounce } from 'usehooks-ts';
import { useTranslation } from 'react-i18next';
import { useAttenderKeyboard } from '@/components/Record/SubjectContent/@hooks/useAttenderKeyboard';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { copyClipBoardText } from '@/utils';
import process from 'process';
import { useSnackbar } from '@/hooks/global-tools';
import ShareDetailButton from '@/components/Record/ShareButton/ShareDetailButton';
import RecommendedShareList from './RecommendedShareList';
import AddAttendeeButton from '@/components/Record/ShareButton/useShareModal/AddButton';
import { Stack } from '@mui/material';

const registerName = 'searchFields.shareAttender';
/** 추천 목록으로 불러올 최근 공유 대상 수 */
const RECOMMENDED_FETCH_SIZE = 20;

const ShareSearch = () => {
  const { isMobilePath } = usePlatform();
  const { t } = useTranslation();
  const [isFocus, setIsFocus] = useState(false);
  const [shareMode, setShareMode] = useState(ShareMode.Share);
  const ref = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [searchFocusIndex, setSearchFocusIndex] = useState(-1);
  const [attenderFocusIndex, setAttenderFocusIndex] = useState(-1);
  const [removedRecommendedIds, setRemovedRecommendedIds] = useState<string[]>(
    [],
  );
  const { watch, getValues, setValue } = useFormContext<RecordFilter>();
  const { data: shareData, mutate } = useTalkFlowShareList(getValues('docId'));
  const { showSuccess } = useSnackbar();

  const copyProfileCardLink = async () => {
    const resultCopy = await copyClipBoardText(
      `${process.env.NEXT_PUBLIC_ORIGIN_URL}/talkflow/${getValues('docId')}`,
    );
    if (resultCopy) {
      showSuccess({
        message: t('문서 링크가 복사되었습니다.'),
        duration: 3000,
      });
    }
  };

  const searchString = watch(registerName);
  const debouncedValue = useDebounce(searchString, 500);
  const { data } = usePassportSearch({ keyword: debouncedValue }, 2);

  const searchData =
    data?.data?.map((res) => ({
      passportId: res.passportId,
      name: res.detail.name,
      departmentName: res.detail.departmentName,
      positionName: res.detail.positionName,
      divisionName: res.detail.divisionName,
      profileImageUrl: res.detail.profileImageUrl,
    })) || [];

  useClickOutside(ref, () => {
    setIsFocus(false);
    setValue(registerName, '');
  });

  const shareAttender = watch('shareAttender');

  // 내부 참석자의 passportId 목록
  const internalPassportIds = useMemo(() => {
    return getValues('internal')?.map((res) => res.passportId) || [];
  }, [getValues('internal')]);

  // 최근 공유 대상 목록 조회 (내부참석자 제외, 최대 20건)
  const { data: recentShareTargets } = useRecentShareTargets({
    excludePassportIds: internalPassportIds,
    listSize: RECOMMENDED_FETCH_SIZE,
  });

  // 이미 공유된 사람과 현재 공유 대기 중인 사람 목록
  const excludePassportIds = useMemo(() => {
    const alreadyShared = shareData?.data.map((res) => res.scope.value) || [];
    const pendingShare = shareAttender.map((res) => res.passportId) || [];
    return [...alreadyShared, ...pendingShare];
  }, [shareData, shareAttender]);

  // 내부 참석자 추가 버튼 표시 여부
  const shouldShowAddInnerButton = useMemo(() => {
    const inner = getValues('internal');
    if (!inner || inner.length === 0) return false;

    const alreadyPassports = [
      ...(shareData?.data.map((res) => res.scope.value) || []),
      ...(shareAttender.map((res) => res.passportId) || []),
    ];

    // 내부 참석자 중 아직 추가되지 않은 사람이 있는지 확인
    const hasUnaddedInner = inner.some(
      (res) => !alreadyPassports.includes(res.passportId),
    );

    return hasUnaddedInner;
  }, [getValues, shareData, shareAttender]);

  // 추천 목록 필터링: 개인(passport)만, 이미 공유된 사람·공유 대기 중인 사람·삭제된 항목 제외 (최신순)
  // 5명까지는 바로 보이고 나머지는 "더보기"로 펼침 (RecommendedShareList)
  // (내부참석자는 서버에서 이미 제외됨)
  const recommendedList = useMemo<InternalParticipant[]>(() => {
    if (!recentShareTargets?.data) return [];

    return recentShareTargets.data
      .filter(isRecentSharePassportTarget)
      .map((target) => target.detail)
      .filter(
        (passport) =>
          !excludePassportIds.includes(passport.passportId) &&
          !removedRecommendedIds.includes(passport.passportId),
      )
      .map((passport) => ({
        passportId: passport.passportId,
        name: passport.detail.name,
        departmentName: passport.detail.departmentName,
        positionName: passport.detail.positionName,
        divisionName: passport.detail.divisionName,
        profileImageUrl: passport.detail.profileImageUrl || '',
      }));
  }, [recentShareTargets, removedRecommendedIds, excludePassportIds]);

  const handleDelete = (passportId: string) => {
    const current = getValues('shareAttender');
    const updated = current.filter(
      (item: InternalParticipant) => item.passportId !== passportId,
    );
    setValue('shareAttender', updated);
    // Reset attender focus if item was deleted
    if (attenderFocusIndex >= updated.length) {
      setAttenderFocusIndex(Math.max(-1, updated.length - 1));
    }
  };

  const onClick = (mode: ShareMode) => {
    setShareMode(mode);
  };

  const disabled = shareAttender.length === 0 || loading;

  const handleClick = async () => {
    try {
      setLoading(true);
      const docId = getValues('docId');
      const shareAttenderList = getValues('shareAttender');
      const temp = shareAttenderList.map((res) => ({
        role: shareMode === ShareMode.Share ? 'sharer' : 'reader',
        scope: {
          type: 'passport',
          value: res.passportId,
        },
      }));
      const param: ShareModeParam = { aclData: temp };
      await useSetTalkFlowShareMode(docId, param);

      // 실제로 초대한 사람 중 내부참석자가 아닌 사람만 최근 공유 목록에 추가
      const internalPassportIds =
        getValues('internal')?.map((res) => res.passportId) || [];
      const passportsToAdd = shareAttenderList
        .filter((res) => !internalPassportIds.includes(res.passportId))
        .map((res) => res.passportId);

      if (passportsToAdd.length > 0) {
        addRecentSharedPassports(passportsToAdd).catch((error) => {
          console.error('Failed to add to recent shared list:', error);
        });
      }

      setValue('shareAttender', []);
      mutate();
    } catch (e) {
      console.log(e);
      alert('잠시후 다시 시도해 주세요.');
    } finally {
      setLoading(false);
    }
  };

  const addInnerAttender = () => {
    const inner = getValues('internal');
    const shareAttender = getValues('shareAttender');

    const alreadyPassports = [
      ...(shareData?.data.map((res) => res.scope.value) || []),
      ...(shareAttender.map((res) => res.passportId) || []),
    ];

    const filtered = inner.filter(
      (res) => !alreadyPassports.includes(res.passportId),
    );

    const before = getValues('shareAttender');
    setValue('shareAttender', [...before, ...filtered]);
  };

  const handleTogglePassport = (passport: InternalParticipant) => {
    const current = getValues('shareAttender');
    const exists = current.some(
      (item) => item.passportId === passport.passportId,
    );
    const updated = exists
      ? current.filter((item) => item.passportId !== passport.passportId)
      : [...current, passport];
    setValue('shareAttender', updated);
    setValue(registerName, '');
  };

  // 추천 목록 클릭 핸들러
  const handleClickRecommended = (participant: InternalParticipant) => {
    const current = getValues('shareAttender');
    const updated = [...current, participant];
    setValue('shareAttender', updated);

    // 추천 목록에서 제거 (재표시 안 됨)
    setRemovedRecommendedIds((prev) => [...prev, participant.passportId]);
  };

  const { handleKeyDown } = useAttenderKeyboard({
    isFocus,
    debouncedValue,
    searchString,
    searchData,
    attenderList: shareAttender,
    searchFocusIndex,
    attenderFocusIndex,
    registerName,
    setSearchFocusIndex,
    setAttenderFocusIndex,
    setIsFocus,
    setValue,
    handleTogglePassport,
    handleDelete,
  });

  // Reset search focus when search data changes
  useEffect(() => {
    setSearchFocusIndex(-1);
  }, [debouncedValue]);

  // Reset attender focus when input has content
  useEffect(() => {
    if (searchString.length > 0) {
      setAttenderFocusIndex(-1);
    }
  }, [searchString]);

  return (
    <div>
      {isMobilePath ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '32px 20px 0px 20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <ShareDetailButton
              shareMode={shareMode}
              onClick={onClick}
              inviteMode={true}
              style={{ marginLeft: '0' }}
            />
            <CopyLinkButton onClick={copyProfileCardLink} />
          </div>
          <ShareSearchInputSection
            ref={ref}
            isFocus={isFocus}
            registerName={registerName}
            shareAttender={shareAttender}
            attenderFocusIndex={attenderFocusIndex}
            handleDelete={handleDelete}
            handleKeyDown={handleKeyDown}
            setIsFocus={setIsFocus}
            debouncedValue={debouncedValue}
            searchData={searchData}
            disableData={shareData?.data}
            searchFocusIndex={searchFocusIndex}
            handleTogglePassport={handleTogglePassport}
          />
          <Stack direction="row" gap={1} sx={{ flexWrap: 'wrap' }}>
            {shouldShowAddInnerButton && (
              <AddAttendeeButton
                onClick={addInnerAttender}
                sx={{
                  display: 'flex',
                  padding: '12px',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {t('내부 참석자')}
              </AddAttendeeButton>
            )}
            <RecommendedShareList
              recommendedList={recommendedList}
              onClickRecommended={handleClickRecommended}
            />
          </Stack>
          <ShareSubmitButton
            disabled={disabled}
            loading={loading}
            onClick={handleClick}
            style={{
              width: '100%',
              marginTop: '8px',
              fontSize: '16px',
              fontWeight: '400',
              lineHeight: '24px',
            }}
          />
        </div>
      ) : (
        <>
          <InviteContainer>
            <ShareSearchInputSection
              ref={ref}
              isFocus={isFocus}
              registerName={registerName}
              shareAttender={shareAttender}
              attenderFocusIndex={attenderFocusIndex}
              handleDelete={handleDelete}
              handleKeyDown={handleKeyDown}
              setIsFocus={setIsFocus}
              debouncedValue={debouncedValue}
              searchData={searchData}
              disableData={shareData?.data}
              searchFocusIndex={searchFocusIndex}
              handleTogglePassport={handleTogglePassport}
              rightChildren={
                <ShareDetailButton
                  shareMode={shareMode}
                  onClick={onClick}
                  inviteMode={true}
                />
              }
            />
            <ShareSubmitButton
              disabled={disabled}
              loading={loading}
              onClick={handleClick}
            />
          </InviteContainer>
          {(shouldShowAddInnerButton || recommendedList.length > 0) && (
            <Stack
              direction="row"
              gap={1}
              sx={{ flexWrap: 'wrap', alignItems: 'center', marginTop: '12px' }}
            >
              {shouldShowAddInnerButton && (
                <AddAttendeeButton onClick={addInnerAttender}>
                  {t('내부 참석자')}
                </AddAttendeeButton>
              )}
              <RecommendedShareList
                recommendedList={recommendedList}
                onClickRecommended={handleClickRecommended}
              />
            </Stack>
          )}
        </>
      )}
    </div>
  );
};

export default ShareSearch;
